/*
 * BikeLink: kategorichips som faner.
 * Klikk på en chip bytter innholdet under chipsene (tittel, produktrutenett, filtre, SEO-tekst)
 * uten full sideinnlasting. Lenkene er vanlige <a href> (SEO / uten JS).
 * - Henter målkolleksjonen (full side, samme domene) og bytter ut alle template-seksjoner unntatt chipsene.
 * - history.pushState + popstate (tilbake-knappen fungerer).
 * - Chipsene blir stående på samme sted i vinduet (scroll-ankring).
 */
(() => {
  if (window.__bikelinkChips) return;
  window.__bikelinkChips = true;

  const NAV_SEL = '.shopify-section--bikelink-collection-nav';
  const cache = new Map();
  let controller = null;
  let renderedPath = location.pathname + location.search;

  const keyOf = (url) => url.pathname + url.search;
  const getNav = (root = document) => root.querySelector(NAV_SEL);

  const siblingsOf = (nav) => {
    const all = Array.from(nav.parentElement.children).filter((el) =>
      el !== nav && el.classList.contains('shopify-section') && /^shopify-section-template--/.test(el.id)
    );
    const pos = (el) => nav.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING ? 'pre' : 'post';
    return { pre: all.filter((el) => pos(el) === 'pre'), post: all.filter((el) => pos(el) === 'post') };
  };

  const fetchPage = (url) => {
    const key = keyOf(url);
    if (!cache.has(key)) {
      const p = fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .catch((e) => { cache.delete(key); throw e; });
      cache.set(key, p);
    }
    return cache.get(key);
  };

  const setActive = (nav, url) => {
    nav.querySelectorAll('.bikelink-chips__chip').forEach((a) => {
      const on = new URL(a.href, location.origin).pathname === url.pathname;
      a.classList.toggle('is-active', on);
      if (on) {
        a.setAttribute('aria-current', 'page');
        // Bare horisontal scroll i chip-raden (mobil) – aldri vertikal scroll av siden
        const sc = a.closest('.bikelink-chips');
        if (sc && sc.scrollWidth > sc.clientWidth) {
          const l = a.offsetLeft - sc.offsetLeft, r = l + a.offsetWidth;
          if (l < sc.scrollLeft) sc.scrollTo({ left: l - 16, behavior: 'smooth' });
          else if (r > sc.scrollLeft + sc.clientWidth) sc.scrollTo({ left: r - sc.clientWidth + 16, behavior: 'smooth' });
        }
      }
      else a.removeAttribute('aria-current');
    });
  };

  const copyHead = (doc) => {
    document.title = doc.title;
    [['meta[name="description"]', 'content'], ['link[rel="canonical"]', 'href'], ['meta[property="og:url"]', 'content'],
     ['meta[property="og:title"]', 'content'], ['meta[property="og:description"]', 'content']].forEach(([sel, attr]) => {
      const src = doc.head.querySelector(sel), dst = document.head.querySelector(sel);
      if (src && dst) dst.setAttribute(attr, src.getAttribute(attr));
    });
  };

  async function swap(url, { push }) {
    const nav = getNav();
    if (!nav) { location.href = url.toString(); return; }
    if (controller) controller.abort();
    const myController = (controller = new AbortController());

    const { pre, post } = siblingsOf(nav);
    nav.classList.add('is-loading');
    nav.setAttribute('aria-busy', 'true');
    [...pre, ...post].forEach((el) => el.classList.add('bikelink-swap-loading'));
    setActive(nav, url);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    let html;
    try {
      html = await fetchPage(url);
    } catch (e) {
      if (myController.signal.aborted) return;
      location.href = url.toString(); // fallback: vanlig navigasjon
      return;
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
    if (myController.signal.aborted) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const targetNav = getNav(doc);
    if (!targetNav) { location.href = url.toString(); return; }

    const anchorTop = nav.getBoundingClientRect().top;
    const t = siblingsOf(targetNav);
    const now = siblingsOf(nav);
    [...now.pre, ...now.post].forEach((el) => el.remove());
    t.pre.forEach((el) => nav.before(document.importNode(el, true)));
    let ref = nav;
    t.post.forEach((el) => { const n = document.importNode(el, true); ref.after(n); ref = n; });

    copyHead(doc);
    if (push) history.pushState({ bikelinkChips: true }, '', url.toString());
    renderedPath = keyOf(new URL(location.href));
    setActive(nav, url);
    nav.classList.remove('is-loading');
    nav.removeAttribute('aria-busy');

    // Hold chipsene på samme sted i vinduet, også mens bilder over chipsene lastes inn
    const keepAnchor = () => {
      const delta = nav.getBoundingClientRect().top - anchorTop;
      if (Math.abs(delta) > 1) window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
    };
    keepAnchor();
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(keepAnchor);
      siblingsOf(nav).pre.forEach((el) => ro.observe(el));
      const stop = () => { ro.disconnect(); window.removeEventListener('wheel', stop); window.removeEventListener('touchmove', stop); };
      window.addEventListener('wheel', stop, { passive: true, once: true });
      window.addEventListener('touchmove', stop, { passive: true, once: true });
      setTimeout(stop, 2500);
    }

    document.dispatchEvent(new CustomEvent('bikelink:collection:swapped', { detail: { url: url.toString() } }));
  }

  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest(NAV_SEL + ' a.bikelink-chips__chip');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || a.target === '_blank') return;
    const url = new URL(a.href, location.origin);
    if (url.origin !== location.origin) return;
    e.preventDefault();
    if (keyOf(url) === keyOf(new URL(location.href))) return;
    swap(url, { push: true });
  });

  // Forhåndshent ved hover/touch for raskere bytte
  const prefetch = (e) => {
    const a = e.target.closest && e.target.closest(NAV_SEL + ' a.bikelink-chips__chip');
    if (!a) return;
    const url = new URL(a.href, location.origin);
    if (keyOf(url) !== keyOf(new URL(location.href))) fetchPage(url).catch(() => {});
  };
  document.addEventListener('pointerenter', prefetch, true);
  document.addEventListener('touchstart', prefetch, { passive: true, capture: true });
  document.addEventListener('focusin', prefetch);

  window.addEventListener('popstate', () => {
    const url = new URL(location.href);
    if (keyOf(url) === renderedPath) return;
    if (!getNav()) return;
    swap(url, { push: false });
  });

  // Vi styrer scroll selv ved tilbake/frem, slik at chipsene blir stående der de var
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  try { history.replaceState(Object.assign({}, history.state, { bikelinkChips: true }), ''); } catch (e) {}
})();
